import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check, ChevronDown, Copy } from 'lucide-react';
import { toast } from 'sonner';

export type ShareEntityType =
  | 'activity'
  | 'producer'
  | 'place'
  | 'accommodation'
  | 'flea_market'
  | 'product'
  | 'curated';

interface EntitySharePanelProps {
  entityType: ShareEntityType;
  publicUrl?: string | null;
  embedSnippet?: string | null;
  socialAction?: ReactNode;
  socialDescription?: string;
  className?: string;
  variant?: 'inline' | 'flat';
}

const ENTITY_LABELS: Record<ShareEntityType, string> = {
  activity: 'aktivitet',
  producer: 'värd',
  place: 'plats',
  accommodation: 'boende',
  flea_market: 'loppis',
  product: 'produkt',
  curated: 'inspirationssida',
};

/** Definite form — used in natural Swedish sentences. */
const ENTITY_LABELS_DEFINITE: Record<ShareEntityType, string> = {
  activity: 'aktiviteten',
  producer: 'värden',
  place: 'platsen',
  accommodation: 'boendet',
  flea_market: 'loppisen',
  product: 'produkten',
  curated: 'inspirationssidan',
};

export const getEntityShareLabel = (entityType: ShareEntityType) => ENTITY_LABELS[entityType];
export const getEntityShareLabelDefinite = (entityType: ShareEntityType) => ENTITY_LABELS_DEFINITE[entityType];

export function EntitySharePanel({
  entityType,
  publicUrl,
  embedSnippet,
  socialAction,
  socialDescription = 'Skapa inlägg för sociala medier.',
  className,
  variant = 'inline',
}: EntitySharePanelProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [embedCopied, setEmbedCopied] = useState(false);

  const linkTimerRef = useRef<number | null>(null);
  const embedTimerRef = useRef<number | null>(null);

  const entityLabel = getEntityShareLabel(entityType);
  const entityLabelDefinite = getEntityShareLabelDefinite(entityType);
  const supportsLinkShare = Boolean(publicUrl);
  const supportsEmbed = Boolean(embedSnippet);
  const supportsSocialShare = Boolean(socialAction);

  const helperCopy = useMemo(() => {
    return `Sprid ${entityLabelDefinite} i dina egna kanaler.`;
  }, [entityLabelDefinite]);

  const clearTimer = (ref: React.MutableRefObject<number | null>) => {
    if (!ref.current) return;
    window.clearTimeout(ref.current);
    ref.current = null;
  };

  const handleCopy = async (
    text: string,
    setCopied: (value: boolean) => void,
    timerRef: React.MutableRefObject<number | null>,
    fallbackError: string,
  ) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      clearTimer(timerRef);
      timerRef.current = window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error(fallbackError);
    }
  };

  useEffect(() => {
    return () => {
      clearTimer(linkTimerRef);
      clearTimer(embedTimerRef);
    };
  }, []);

  const content = (
    <div className={cn(variant === 'flat' ? 'space-y-3' : 'px-4 pb-4 pt-1 space-y-3')}>
      <p className="text-xs text-muted-foreground">Dela {entityLabelDefinite} via länk, hemsida eller sociala medier.</p>

      {supportsLinkShare && (
        <div className="rounded-md bg-muted/50 p-3 space-y-2">
          <p className="text-xs font-medium text-foreground">Kopiera länk</p>
          <p className="text-[11px] text-muted-foreground truncate">{publicUrl}</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleCopy(publicUrl!, setLinkCopied, linkTimerRef, 'Kunde inte kopiera länken')}
          >
            {linkCopied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
            {linkCopied ? 'Kopierad' : 'Kopiera länk'}
          </Button>
        </div>
      )}

      {supportsEmbed && (
        <div className="rounded-md bg-muted/50 p-3 space-y-2">
          <p className="text-xs font-medium text-foreground">Bädda in på din hemsida</p>
          <pre className="rounded-md bg-muted/60 p-3 text-[11px] leading-relaxed text-muted-foreground overflow-x-auto whitespace-pre">
            {embedSnippet}
          </pre>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => handleCopy(embedSnippet!, setEmbedCopied, embedTimerRef, 'Kunde inte kopiera koden')}
          >
            {embedCopied ? <Check className="h-3.5 w-3.5 mr-1.5" /> : <Copy className="h-3.5 w-3.5 mr-1.5" />}
            {embedCopied ? 'Kopierad' : 'Kopiera kod'}
          </Button>
        </div>
      )}

      {supportsSocialShare && (
        <div className="rounded-md bg-muted/50 p-3 space-y-2">
          <p className="text-xs font-medium text-foreground">Skapa inlägg för sociala medier</p>
          <p className="text-[11px] text-muted-foreground">{socialDescription}</p>
          {socialAction}
        </div>
      )}
    </div>
  );

  if (variant === 'flat') {
    return <div className={className}>{content}</div>;
  }

  return (
    <section className={cn('rounded-xl bg-muted/30 border border-border/30 shadow-none', className)}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className="w-full text-left px-4 py-3.5 flex items-center justify-between gap-3 hover:bg-muted/20 transition-colors rounded-xl"
        aria-expanded={isOpen}
      >
        <div className="min-w-0">
          <h3 className="text-sm font-medium text-foreground">Dela {entityLabel}</h3>
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{helperCopy}</p>
        </div>
        <ChevronDown
          className={cn(
            'h-4 w-4 text-muted-foreground shrink-0 transition-transform duration-300 ease-out',
            isOpen && 'rotate-180',
          )}
        />
      </button>

      <div
        className={cn(
          'overflow-hidden transition-all duration-300 ease-out',
          isOpen ? 'max-h-[720px] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        {content}
      </div>
    </section>
  );
}
